import { ChevronLeft, ChevronRight } from '@material-ui/icons';
import React, { useState } from 'react';
import { useBoolean } from 'react-use-boolean';
import addIcon from '../../assets/img/add.svg';
import fullscreenIcon from '../../assets/img/maximize.svg';
import { reorderItemList } from '../../libs/reorderItemList';
import { Button } from '../common/Button';
import { Container } from '../common/Container';
import { Dialog, DialogTitle } from '../common/Dialog';
import { IconButton } from '../common/IconButton';
import { Loader } from '../common/Loader';
import { Typography } from '../common/Typography';
import { resetCameraRotation } from './AframeItems';
import { CreateMonoPanoramaGalleryModal } from './CreateMonoPanoramaGallery';
import { useGalleryContext } from './GalleryContext';
import { useGalleryViewStyles } from './galleryViewStyles';
import { getGalleryEditorUrl } from './getGalleryUrl';
import { LeaveGalleryEditor } from './LeaveGalleryEditor';
import { PanoramaThumbnailDnD } from './PanoramaThumbnailDnD';
import { PanoramaThumbnailViewer } from './PanoramaThumbnailViewer';
import { PanoramaViewer } from './PanoramaViewer';
import { PanoramaViewerWaypoint } from './PanoramaViewerWaypoint';
import { SaveGallery } from './SaveGallery';
import { TourMode } from './TourMode';

const Thumbnails: React.FC = () => {
  const { newPanoramas, setState, currentPanorama, currentPanoramaIndex, viewMode } =
    useGalleryContext();

  return (
    <PanoramaThumbnailViewer panoramas={newPanoramas} currentPanoramaIndex={currentPanoramaIndex}>
      {(viewerProps) => (
        <PanoramaThumbnailDnD
          {...viewerProps}
          currentPanorama={currentPanorama}
          onPanoramaClick={(panorama) =>
            setState((prevState) => {
              return { ...prevState, currentPanorama: panorama };
            })
          }
          onPanoramaDeleteClick={(panorama) =>
            setState((prevState) => ({
              ...prevState,
              newPanoramas: newPanoramas.filter((p) => p !== panorama),
            }))
          }
          panoramas={newPanoramas}
          onPanoramaMoved={({ oldPosition, newPosition }) => {
            const result = reorderItemList(newPanoramas, { oldPosition, newPosition });
            setState((prevState) => ({ ...prevState, newPanoramas: result }));
          }}
          displayActions={viewMode === 'edit'}
        />
      )}
    </PanoramaThumbnailViewer>
  );
};

const PreviousButton: React.FC = () => {
  const { previousPanoramaIndex, goToPanorama, showNavigationButtons } = useGalleryContext();
  const goToPrevious = () => {
    goToPanorama(previousPanoramaIndex);
  };

  return showNavigationButtons ? (
    <>
      <IconButton
        onClick={goToPrevious}
        aria-label='previous panorama'
        icon={<ChevronLeft />}
        variant={'basic'}
      />
      <Typography>Previous</Typography>
    </>
  ) : null;
};

const NextButton: React.FC = () => {
  const { goToPanorama, nextPanoramaIndex, showNavigationButtons } = useGalleryContext();

  const goToNext = () => {
    goToPanorama(nextPanoramaIndex);
  };

  return showNavigationButtons ? (
    <>
      <IconButton
        onClick={goToNext}
        aria-label='next panorama'
        icon={<ChevronRight />}
        variant={'basic'}
      />
      <Typography>Next</Typography>
    </>
  ) : null;
};

const PanoramaFrame: React.FC = () => {
  const {
    currentPanorama,
    newPanoramas,
    currentPanoramaIndex,
    navigablePanoramas,
    goToPanorama,
    waypointsShown,
  } = useGalleryContext();

  if (!currentPanorama) {
    return null;
  }

  return (
    <div title={currentPanorama.title} aria-label='current panorama view'>
      <PanoramaViewer
        currentPanoramaIndex={currentPanoramaIndex}
        currentPanorama={currentPanorama}
        panoramas={newPanoramas}
        resetCameraRotation={resetCameraRotation}
      >
        {navigablePanoramas.map(({ panorama, index }, arrayIndex) => (
          <PanoramaViewerWaypoint
            key={arrayIndex}
            waypointOption={waypointsShown}
            panorama={panorama}
            currentPanorama={currentPanorama}
            goToPanorama={goToPanorama}
            currentPanoramaIndex={currentPanoramaIndex}
            panoramaIndex={index}
            totalPanoramas={newPanoramas.length}
          />
        ))}
      </PanoramaViewer>
    </div>
  );
};

const AddPanoramaButton: React.FC = () => {
  const { newPanoramas, currentFolder, setState } = useGalleryContext();
  const [open, actions] = useBoolean();

  if (!currentFolder) {
    return null;
  }
  return (
    <>
      <IconButton
        onClick={actions.on}
        icon={<img src={addIcon} alt={'add panorama'} />}
        variant={'primary'}
        style={{ marginRight: 8 }}
      />
      <Dialog open={open} onClose={actions.off}>
        <DialogTitle>Add Panoramas</DialogTitle>
        <CreateMonoPanoramaGalleryModal
          minNumberOfSelection={1}
          descriptionText='Select at least 1 panorama exported from the same project and then confirm your selection.'
          initialPanoramas={newPanoramas}
          currentFolder={currentFolder}
          onClose={actions.off}
          onConfirm={(selectedNewPanoramas) => {
            actions.off();
            setState((state) => ({
              ...state,
              newPanoramas: state.newPanoramas.concat(selectedNewPanoramas),
            }));
          }}
          confirmText='Add Selection'
        />
      </Dialog>
    </>
  );
};

const GoToEdit: React.FC = () => {
  const { currentGallery } = useGalleryContext();
  if (!currentGallery) {
    return null;
  }
  return (
    <Button onClick={() => window.location.assign(getGalleryEditorUrl(currentGallery))}>
      Edit
    </Button>
  );
};

export const GalleryView: React.FC = () => {
  const [panoramaBar, setPanoramaBar] = useState<'visible' | 'folded' | 'hidden'>('folded');
  const styles = useGalleryViewStyles();
  const { currentFolder, viewMode, isError, isLoading } = useGalleryContext();
  const handleDrawerClick = () =>
    panoramaBar === 'hidden' ? setPanoramaBar('folded') : setPanoramaBar('hidden');

  if (isError) {
    return (
      <div className={styles.error}>
        <Typography variant='h2'>Gallery not found</Typography>
        <Typography>
          This Gallery seems to have been deleted or not shared anymore by the owner. Please contact
          the creator of this Gallery for a new link.
        </Typography>
      </div>
    );
  }

  if (isLoading) {
    return (
      <Container>
        <Loader />
      </Container>
    );
  }

  return (
    <div>
      {viewMode === 'read' && (
        <div className={styles.topBar}>
          <GoToEdit />
        </div>
      )}
      {viewMode === 'edit' && currentFolder ? (
        <div className={styles.topBar}>
          <TourMode />
          <AddPanoramaButton />
          <SaveGallery />
          <LeaveGalleryEditor />
        </div>
      ) : null}
      <div
        className={`${styles.bottomBar} ${
          panoramaBar === 'hidden'
            ? styles.moveDown
            : panoramaBar === 'visible'
            ? styles.moveUp
            : styles.folded
        }`}
      >
        <button
          className={styles.hidePanoramasButton}
          onClick={handleDrawerClick}
          aria-label={'hide panoramas'}
        />
        <div
          className={panoramaBar === 'hidden' ? styles.fadeOut : undefined}
          onMouseOver={() => setPanoramaBar('visible')}
          onMouseLeave={() => setPanoramaBar('folded')}
          aria-label={'panorama bar'}
        >
          <Thumbnails />
        </div>
      </div>
      <div className={styles.previous}>
        <PreviousButton />
      </div>
      <div className={styles.next}>
        <NextButton />
      </div>
      <div className={styles.panorama}>
        <PanoramaFrame />
        <div
          className={styles.fullScreenToggle}
          onClick={() => document.querySelector('body')?.requestFullscreen()}
        >
          <IconButton
            variant='outlined'
            icon={<img src={fullscreenIcon} alt={'switch to fullscreen'} />}
          />
        </div>
      </div>
    </div>
  );
};
